/**
 * Isomorphic Draft/Config state logic for the Mieterselbstauskunft generator
 * (App-native Vorlagen-Spec 2026-07-08 §2). Extracted 1:1 from
 * `components/generator-form.tsx` so the Marketing wizard AND the App-native
 * settings form can share the exact same pure state machine while keeping
 * separate UIs. Pure – no React, no `'use client'`. Imports only from
 * `./config.schema` and `./fields` (the same relative imports the wizard had).
 */
import {
  SELF_DISCLOSURE_CONFIG_VERSION,
  type SelfDisclosureConfig,
  type SelfDisclosureConfigAuthor,
  SelfDisclosureConfigAuthorSchema,
} from './config.schema';
import {
  type IncomeVariant,
  type LocalizedText,
  SELF_DISCLOSURE_FIELDS,
  SELF_DISCLOSURE_PRESETS,
  type SelfDisclosureField,
  type SelfDisclosurePreset,
  type SelfDisclosurePresetKey,
  getFieldByKey,
  getPresetByKey,
  presetName,
  zweisprachigerText,
} from './fields';

type Language = SelfDisclosureConfig['language'];

export interface GeneratorDraft {
  language: Language;
  personCount: number;
  /** Optional extra answer/signature column for a guarantor (§2). */
  includeGuarantor: boolean;
  enabledFields: string[];
  fieldOptions: {
    incomeVariant?: IncomeVariant;
    incomeThresholdEur?: number;
    isSocialHousing: boolean;
    publicPayerDirect: boolean;
  };
  sender: {
    displayName: string;
    isCompany: boolean;
    contactPerson: string;
    street: string;
    postalCode: string;
    city: string;
    email: string;
    dpoContact: string;
    propertyLabel: string;
  };
}

export interface DerivedState {
  /**
   * The current valid config, or null while incomplete/invalid. Typed as the
   * strict Author config (Spec 2026-07-08 §3.2 #5): `deriveState` validates
   * against the Author schema, so a non-null `config` is guaranteed to carry
   * the mandatory address – which lets the write actions (Create/Update/Park/
   * Save, all Author-typed) consume it directly. It is still assignable to the
   * loose `SelfDisclosureConfig` (Author ⊆ loose) for the render entry points.
   */
  config: SelfDisclosureConfigAuthor | null;
  /** True when a valid config can be produced. */
  isComplete: boolean;
  /** Human-readable German validation messages (empty when valid). */
  errors: string[];
}

// ── Draft helpers ─────────────────────────────────────────────────────────────

export function emptySender(): GeneratorDraft['sender'] {
  return {
    displayName: '',
    isCompany: false,
    contactPerson: '',
    street: '',
    postalCode: '',
    city: '',
    email: '',
    dpoContact: '',
    propertyLabel: '',
  };
}

/**
 * Default = the recommended preset "nach_besichtigung", minus the
 * threshold-confirmation field: it requires a concrete threshold amount, so
 * including it by default would render the initial config invalid (the
 * marketing island wants a valid config out of the box). Applying the preset
 * explicitly DOES include it (the user then enters the amount).
 */
export function defaultDraft(): GeneratorDraft {
  const preset = SELF_DISCLOSURE_PRESETS.find(
    (candidate) => candidate.key === 'nach_besichtigung',
  );

  const draft: GeneratorDraft = {
    language: 'de',
    personCount: 1,
    includeGuarantor: false,
    enabledFields: (preset?.fieldKeys ?? []).filter(
      (key) => key !== 'income_threshold_met',
    ),
    fieldOptions: { isSocialHousing: false, publicPayerDirect: false },
    sender: emptySender(),
  };

  return sanitizeEnabled(draft);
}

export function configToDraft(config: SelfDisclosureConfig): GeneratorDraft {
  const s = config.sender;

  return {
    language: config.language,
    personCount: config.personCount,
    includeGuarantor: config.includeGuarantor,
    enabledFields: [...config.enabledFields],
    fieldOptions: {
      incomeVariant: config.fieldOptions.incomeVariant,
      incomeThresholdEur: config.fieldOptions.incomeThresholdEur,
      isSocialHousing: config.fieldOptions.isSocialHousing,
      publicPayerDirect: config.fieldOptions.publicPayerDirect,
    },
    sender: {
      displayName: s.displayName,
      isCompany: s.isCompany,
      contactPerson: s.contactPerson ?? '',
      street: s.street ?? '',
      postalCode: s.postalCode ?? '',
      city: s.city ?? '',
      email: s.email ?? '',
      dpoContact: s.dpoContact ?? '',
      propertyLabel: s.propertyLabel ?? '',
    },
  };
}

function trimmedOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
}

export function draftToCandidate(draft: GeneratorDraft): unknown {
  return {
    version: SELF_DISCLOSURE_CONFIG_VERSION,
    templateType: 'self_disclosure' as const,
    language: draft.language,
    personCount: draft.personCount,
    includeGuarantor: draft.includeGuarantor,
    enabledFields: draft.enabledFields,
    fieldOptions: {
      incomeVariant: draft.fieldOptions.incomeVariant,
      incomeThresholdEur: draft.fieldOptions.incomeThresholdEur,
      isSocialHousing: draft.fieldOptions.isSocialHousing,
      publicPayerDirect: draft.fieldOptions.publicPayerDirect,
    },
    sender: {
      displayName: draft.sender.displayName.trim(),
      isCompany: draft.sender.isCompany,
      contactPerson: trimmedOrUndefined(draft.sender.contactPerson),
      street: trimmedOrUndefined(draft.sender.street),
      postalCode: trimmedOrUndefined(draft.sender.postalCode),
      city: trimmedOrUndefined(draft.sender.city),
      email: trimmedOrUndefined(draft.sender.email),
      dpoContact: trimmedOrUndefined(draft.sender.dpoContact),
      propertyLabel: trimmedOrUndefined(draft.sender.propertyLabel),
    },
  };
}

export function deriveState(draft: GeneratorDraft): DerivedState {
  // Parse against the STRICT Author schema (Spec 2026-07-08 §3.2 #5): only it
  // rejects a missing Straße/PLZ/Ort, so the three address messages surface in
  // `errors` and `firstErrorField` can point at them – otherwise the new
  // required-address errors would be invisible in the UI (the exact anti-case
  // documented at `firstErrorField`). An Author config is assignable to the
  // loose `SelfDisclosureConfig` (Author ⊆ loose), so `config` keeps its type.
  const result = SelfDisclosureConfigAuthorSchema.safeParse(
    draftToCandidate(draft),
  );

  if (result.success) {
    return { config: result.data, isComplete: true, errors: [] };
  }

  const errors = result.error.issues.map((issue) => issue.message);

  return { config: null, isComplete: false, errors };
}

/** Reason a field is locked in the current draft, or null if selectable. */
export function lockReason(
  field: SelfDisclosureField,
  draft: GeneratorDraft,
): string | null {
  if (field.socialHousingOnly && !draft.fieldOptions.isSocialHousing) {
    return 'Nur bei einer öffentlich geförderten Wohnung (Option „Sozialwohnung“).';
  }

  if (field.incomeVariant && draft.fieldOptions.publicPayerDirect) {
    return 'Entfällt, wenn die Miete vollständig direkt von einer öffentlichen Stelle gezahlt wird.';
  }

  return null;
}

/** Removes now-invalid selections after an option change / preset apply. */
export function sanitizeEnabled(draft: GeneratorDraft): GeneratorDraft {
  const enabled = draft.enabledFields.filter((key) => {
    const field = getFieldByKey(key);

    return field ? lockReason(field, draft) === null : false;
  });

  const stillHasThreshold = enabled.some(
    (key) => getFieldByKey(key)?.incomeVariant === 'threshold',
  );
  const stillHasAnyIncome = enabled.some(
    (key) => getFieldByKey(key)?.incomeVariant,
  );

  return {
    ...draft,
    enabledFields: enabled,
    fieldOptions: {
      ...draft.fieldOptions,
      incomeVariant: stillHasAnyIncome
        ? draft.fieldOptions.incomeVariant
        : undefined,
      incomeThresholdEur: stillHasThreshold
        ? draft.fieldOptions.incomeThresholdEur
        : undefined,
    },
  };
}

/** Catalog order for a set of selected keys. */
export function orderedSelection(selected: ReadonlySet<string>): string[] {
  return SELF_DISCLOSURE_FIELDS.filter((field) => selected.has(field.key)).map(
    (field) => field.key,
  );
}

export function currentIncomeVariant(
  keys: readonly string[],
): IncomeVariant | undefined {
  for (const key of keys) {
    const variant = getFieldByKey(key)?.incomeVariant;

    if (variant) return variant;
  }

  return undefined;
}

// ── Income single-choice (Spec §3) ────────────────────────────────────────────

export type IncomeChoice = 'amount' | 'threshold' | 'none';

/** The two mutually-exclusive income field keys, derived from the catalog. */
export const INCOME_KEYS = {
  amount: SELF_DISCLOSURE_FIELDS.find((f) => f.incomeVariant === 'amount')!.key,
  threshold: SELF_DISCLOSURE_FIELDS.find(
    (f) => f.incomeVariant === 'threshold',
  )!.key,
};

/**
 * The radio value is a pure VIEW over `enabledFields` (Spec §3.1) – never a
 * parallel state. Preset apply, sanitizeEnabled and publicPayerDirect-lock all
 * flow through the same field selection, so the radio can never drift.
 */
export function currentIncomeChoice(draft: GeneratorDraft): IncomeChoice {
  return currentIncomeVariant(draft.enabledFields) ?? 'none';
}

/**
 * Mutate the draft from a radio choice (Spec §3.2). Runs through the SAME
 * orderedSelection + sanitizeEnabled + currentIncomeVariant helpers as
 * `toggleField`, guaranteeing byte-identical enabledFields/incomeVariant/
 * incomeThresholdEur states (Spec §3.3 equivalence proof).
 */
export function setIncomeChoice(
  draft: GeneratorDraft,
  choice: IncomeChoice,
): GeneratorDraft {
  const selected = new Set(draft.enabledFields);

  // Exclusive: always drop both income keys first, then add the chosen one.
  selected.delete(INCOME_KEYS.amount);
  selected.delete(INCOME_KEYS.threshold);

  if (choice === 'amount') selected.add(INCOME_KEYS.amount);
  if (choice === 'threshold') selected.add(INCOME_KEYS.threshold);

  const orderedKeys = orderedSelection(selected);

  return sanitizeEnabled({
    ...draft,
    enabledFields: orderedKeys,
    fieldOptions: {
      ...draft.fieldOptions,
      incomeVariant: currentIncomeVariant(orderedKeys),
    },
  });
}

// ── Validation message anchors (Spec §I.2) ────────────────────────────────────

export const DISPLAY_NAME_MESSAGE =
  'Bitte gib den Namen des Vermieters oder der Firma an.';
export const EMAIL_MESSAGE = 'Bitte gib eine gültige E-Mail-Adresse an.';

// Address anchors (Spec 2026-07-08 §3.1) – must match the Author-schema
// messages BYTE-for-byte so `firstErrorField`/`CONTEXTUAL_MESSAGES` recognise
// them.
export const STREET_MESSAGE = 'Bitte gib die Straße und Hausnummer an.';
export const POSTAL_CODE_MESSAGE = 'Bitte gib die Postleitzahl an.';
export const CITY_MESSAGE = 'Bitte gib den Ort an.';

/** Messages shown contextually at their control (not in the fallback block). */
export const CONTEXTUAL_MESSAGES = new Set([
  DISPLAY_NAME_MESSAGE,
  STREET_MESSAGE,
  POSTAL_CODE_MESSAGE,
  CITY_MESSAGE,
  EMAIL_MESSAGE,
]);

/** The threshold message carries a stable prefix (config.schema.ts:114-115). */
export function isThresholdMessage(message: string): boolean {
  return message.startsWith('Für die Betragsgrenzen-Bestätigung');
}

export type SelfDisclosureErrorField =
  | 'income-threshold'
  | 'sender-display-name'
  | 'sender-street'
  | 'sender-postal-code'
  | 'sender-city'
  | 'sender-email';

/**
 * Semantic first error field in catalog order (fields before sender), WITHOUT
 * wizard step and WITHOUT data-test prefix. Pure derivation from draft + errors
 * (the same Zod errors from deriveState – never a custom email regex), so no
 * Zod-rejected address can stay invisible (MUST-FIX #1/V3). Address order
 * (Spec 2026-07-08 §3.3): displayName → street → postalCode → city → email.
 */
export function firstErrorField(
  draft: GeneratorDraft,
  errors: string[],
): SelfDisclosureErrorField | null {
  const choice = currentIncomeChoice(draft);

  if (
    choice === 'threshold' &&
    draft.fieldOptions.incomeThresholdEur === undefined
  ) {
    return 'income-threshold';
  }

  if (draft.sender.displayName.trim() === '') return 'sender-display-name';

  if (errors.includes(STREET_MESSAGE)) return 'sender-street';

  if (errors.includes(POSTAL_CODE_MESSAGE)) return 'sender-postal-code';

  if (errors.includes(CITY_MESSAGE)) return 'sender-city';

  if (errors.includes(EMAIL_MESSAGE)) return 'sender-email';

  return null;
}

// ── Live summary derivation (Spec §2.6/M7) ────────────────────────────────────

/*
 * 🔴 `LANGUAGE_SUMMARY_LABEL` ist am 31.08.2026 hier ENTFALLEN, nicht nur
 * unbenutzt geworden.
 *
 * Es war ein `Record<Language, string>` mit drei deutschen Woertern
 * (Deutsch/Englisch/Zweisprachig) in einer Datei ohne Uebersetzungszugang — und
 * genau das war der Fehler: eine Beschriftung, die in einer reinen Logik-Datei
 * wohnt, kann nie uebersetzt werden, und der einzige Verbraucher
 * (`generator-form.tsx`, Website-Fassung) hat sie deshalb unuebersetzt auf eine
 * oeffentliche Seite geschrieben.
 *
 * Die Beschriftung steht jetzt dort, wo sie hingehoert: im Katalog
 * (`self-disclosure:generator.sprache.*`), und zwar in DENSELBEN Schluesseln,
 * die die Sprachauswahl darueber schon benutzt. Wer hier wieder eine
 * Beschriftungs-Tabelle anlegt, baut die Falle neu auf.
 */

/** The preset key exactly matching the current field selection, else null. */
export function detectPresetKey(
  draft: GeneratorDraft,
): SelfDisclosurePresetKey | null {
  const match = SELF_DISCLOSURE_PRESETS.find((preset) => {
    const applied = applyPreset(draft, preset).enabledFields;

    return (
      applied.length === draft.enabledFields.length &&
      applied.every((key) => draft.enabledFields.includes(key))
    );
  });

  return match?.key ?? null;
}

/**
 * Der Rückfall, wenn die Auswahl zu keinem Satz passt — zweisprachig wie die
 * Sätze selbst.
 *
 * 🔴 Er stand hart als `'Individuell'` im Quelltext, direkt neben einem
 * Generator, der „Deutsch · English · Beides" anbietet. In einem englischen
 * Formular las die Zusammenfassung damit ein deutsches Wort, das nirgends
 * sonst vorkam.
 */
const INDIVIDUELL: LocalizedText = { de: 'Individuell', en: 'Custom' };

/**
 * Preset name matching the current selection, or the "Individuell"-Rückfall
 * (Spec §2.6) — in der Sprache des FORMULARS, nicht der Oberfläche.
 *
 * Die Sprache kommt aus dem Entwurf selbst: der Name steht in der
 * Zusammenfassung eines Dokuments, das gerade in dieser Sprache entsteht.
 * Für `de` bleibt das Ergebnis Zeichen für Zeichen das bisherige.
 */
export function detectPresetName(draft: GeneratorDraft): string {
  const key = detectPresetKey(draft);
  const preset = key ? getPresetByKey(key) : undefined;

  return preset
    ? presetName(preset, draft.language)
    : zweisprachigerText(INDIVIDUELL, draft.language);
}

export function toggleField(
  draft: GeneratorDraft,
  field: SelfDisclosureField,
): GeneratorDraft {
  const selected = new Set(draft.enabledFields);

  if (selected.has(field.key)) {
    selected.delete(field.key);
  } else {
    // Income capture variants are mutually exclusive: enabling one removes
    // the other and records the chosen variant (config superRefine).
    if (field.incomeVariant) {
      for (const other of SELF_DISCLOSURE_FIELDS) {
        if (other.incomeVariant && other.key !== field.key) {
          selected.delete(other.key);
        }
      }
    }

    selected.add(field.key);
  }

  const orderedKeys = orderedSelection(selected);

  return sanitizeEnabled({
    ...draft,
    enabledFields: orderedKeys,
    fieldOptions: {
      ...draft.fieldOptions,
      incomeVariant: currentIncomeVariant(orderedKeys),
    },
  });
}

export function applyPreset(
  draft: GeneratorDraft,
  preset: SelfDisclosurePreset,
): GeneratorDraft {
  const keys = [...preset.fieldKeys];

  return sanitizeEnabled({
    ...draft,
    enabledFields: keys,
    fieldOptions: {
      ...draft.fieldOptions,
      incomeVariant: currentIncomeVariant(keys),
    },
  });
}
